import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { radius } from '@/shared/theme';
import { Icon } from './icon';
import { TileIconButton } from './tile-icon-button';

interface SideSheetProps {
  open: boolean;
  onClose: () => void;
  kicker?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Pinned action row at the bottom (save / cancel). */
  footer?: ReactNode;
  width?: number;
  children: ReactNode;
  expandable?: boolean;
}

/** Right-hand sheet for details and forms (location detail, targets, user form). */
export function SideSheet({
  open,
  onClose,
  kicker,
  title,
  subtitle,
  footer,
  width = 480,
  children,
  expandable,
}: SideSheetProps) {
  const { t } = useTranslation();
  const [expand, setExpand] = useState<boolean>(false);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            maxWidth: '100vw',
            background: theme.vars.palette.md.panel,
            borderLeft: `1px solid ${theme.vars.palette.md.border}`,
            boxShadow: theme.vars.palette.md.shadowLg,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
          }),
        },
        transition: { onExited: () => setExpand(false) },
      }}
    >
      <Box
        sx={(theme) => ({
          width: expand ? { xs: width, sm: `min(calc(100vw - 64px), 1000px)` } : width,
          maxWidth: '100vw',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create('width'),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: '18px 20px 16px',
            borderBottom: `1px solid ${theme.vars.palette.md.border}`,
          })}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {kicker && (
              <Typography variant="kicker" component="div">
                {kicker}
              </Typography>
            )}
            <Typography variant="h2" component="h2" sx={{ mt: kicker ? '4px' : 0 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {expandable && (
            <ButtonBase
              onClick={() => setExpand((p) => !p)}
              sx={(theme) => ({
                display: { xs: 'none', sm: 'inline-flex' },
                position: 'absolute',
                top: '50%',
                right: '100%',
                transform: 'translateY(-50%)',
                writingMode: 'vertical-rl',
                gap: 0.75,
                px: 0.75,
                py: 1.5,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '.04em',
                color: theme.vars.palette.md.muted,
                backgroundColor: theme.vars.palette.md.panel,
                border: `1px solid ${theme.vars.palette.md.border}`,
                borderRight: 0,
                borderRadius: `${radius.tab} 0 0 ${radius.tab}`,
                transition: 'color .14s, background-color .14s',
                '&:hover': {
                  background: theme.vars.palette.md.panel2,
                  color: theme.vars.palette.md.text,
                },
                '&.Mui-focusVisible': {
                  outline: `2px solid ${theme.vars.palette.primary.main}`,
                  outlineOffset: 2,
                },
              })}
            >
              <Icon name={expand ? 'chevronsR' : 'chevronsL'} size={14} />
              {expand ? t('nav.collapse') : t('nav.expand')}
            </ButtonBase>
          )}
          <TileIconButton label={t('actions.close')} onClick={onClose} size={38}>
            <Icon name="x" size={18} />
          </TileIconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: '18px 20px 24px' }}>{children}</Box>
        {footer && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: '14px 20px',
              borderTop: `1px solid ${theme.vars.palette.md.border}`,
              background: theme.vars.palette.md.panel2,
            })}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
